import { useState } from "react";
import S from "../styles/styles";

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtC = (n) => `$${parseFloat(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().split("T")[0];
const addDays = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().split("T")[0]; };
const formatDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m)-1]} ${parseInt(d)}, ${y}`;
};
const currentPeriod = () => new Date().toLocaleString("en-CA", { month: "long", year: "numeric" });
const nextInvoiceNum = (invoices) => {
  const max = invoices.reduce((m, inv) => Math.max(m, parseInt((inv.number || "INV-000").split("-")[1] || 0)), 0);
  return `INV-${String(max + 1).padStart(3, "0")}`;
};
const MANAGER_EMAIL = process.env.REACT_APP_MANAGER_EMAIL || "vbhattad@gmail.com";

// ── Invoice HTML Generator ─────────────────────────────────────────────────
const generateInvoiceHtml = (inv) => {
  const itemRows = (inv.items || []).map((item) =>
    `<tr>
      <td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;font-size:13px">${item.description || ""}</td>
      <td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;text-align:right;font-size:13px;font-weight:600">${fmtC(item.amount)}</td>
    </tr>`
  ).join("");
  const statusColors = { Draft: "#999", Sent: "#2A7BE8", Paid: "#22AA55", Overdue: "#FF2D2D" };
  const sc = statusColors[inv.status] || "#999";
  return [
    '<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Invoice ' + (inv.number || "") + '</title>',
    '<style>',
    '*{margin:0;padding:0;box-sizing:border-box}',
    'body{font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;background:#fff;padding:48px}',
    '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:24px;border-bottom:3px solid #E8622A;margin-bottom:32px}',
    '.logo{font-size:26px;font-weight:900;letter-spacing:-0.5px}.logo span{color:#E8622A}',
    '.logo-sub{font-size:11px;color:#999;margin-top:4px}',
    '.inv-meta{text-align:right}.inv-num{font-size:22px;font-weight:800}',
    '.inv-period{font-size:12px;color:#999;margin-top:2px}',
    '.status{display:inline-block;margin-top:8px;padding:4px 14px;border-radius:99px;font-size:11px;font-weight:700;background:' + sc + '18;color:' + sc + '}',
    '.parties{display:flex;gap:48px;margin-bottom:28px}',
    '.party-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#aaa;margin-bottom:8px}',
    '.party-name{font-size:15px;font-weight:700;margin-bottom:3px}.party-detail{font-size:12px;color:#666;line-height:1.7}',
    '.dates{display:flex;margin-bottom:28px;background:#f8f8f8;border-radius:8px;overflow:hidden}',
    '.date-cell{flex:1;padding:14px 18px;border-right:1px solid #eee}.date-cell:last-child{border-right:none}',
    '.date-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#aaa;margin-bottom:4px}',
    '.date-value{font-size:13px;font-weight:600}',
    '.table-wrap{border:1px solid #eee;border-radius:8px;overflow:hidden;margin-bottom:24px}',
    'table{width:100%;border-collapse:collapse}',
    'thead th{background:#f8f8f8;padding:10px 14px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#888;border-bottom:2px solid #eee}',
    'thead th:last-child{text-align:right}',
    '.total-row td{padding:16px 14px;font-weight:800;font-size:15px;background:#1a1a1a;color:#fff}',
    '.total-row td:last-child{color:#E8622A;font-size:20px;text-align:right}',
    '.notes{background:#fffbf5;border:1px solid #f0e0c8;border-radius:8px;padding:14px 18px;margin-bottom:24px;font-size:12px;color:#666;line-height:1.6}',
    '.payment{border:2px solid #E8622A22;border-radius:10px;padding:20px 24px;margin-bottom:24px;background:#fff8f5}',
    '.payment-title{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#E8622A;margin-bottom:12px}',
    '.prow{display:flex;gap:8px;padding:7px 0;font-size:13px;border-bottom:1px solid #f0ede8}.prow:last-child{border-bottom:none}',
    '.pk{font-weight:700;min-width:160px;color:#1a1a1a}.pv{color:#444}',
    '.footer{text-align:center;font-size:11px;color:#aaa;margin-top:32px;padding-top:20px;border-top:1px solid #f0f0f0;line-height:1.8}',
    '@media print{body{padding:20px}}',
    '</style></head><body>',
    '<div class="header">',
    '<div><div class="logo">prop<span>desk</span></div><div class="logo-sub">Property Management Services · Vancouver, BC</div></div>',
    '<div class="inv-meta"><div class="inv-num">' + (inv.number || "") + '</div><div class="inv-period">' + (inv.period || "") + '</div><div class="status">' + (inv.status || "Draft") + '</div></div>',
    '</div>',
    '<div class="parties">',
    '<div style="flex:1"><div class="party-label">From</div><div class="party-name">PropDesk Property Management</div><div class="party-detail">Vancouver, BC<br/>' + MANAGER_EMAIL + '</div></div>',
    '<div style="flex:1"><div class="party-label">Bill To</div><div class="party-name">' + (inv.ownerName || "") + '</div><div class="party-detail">' + (inv.ownerEmail || "") + '<br/>' + (inv.property || "") + '</div></div>',
    '</div>',
    '<div class="dates">',
    '<div class="date-cell"><div class="date-label">Invoice Date</div><div class="date-value">' + formatDate(inv.issueDate) + '</div></div>',
    '<div class="date-cell"><div class="date-label">Due Date</div><div class="date-value">' + formatDate(inv.dueDate) + '</div></div>',
    '<div class="date-cell"><div class="date-label">Service Period</div><div class="date-value">' + (inv.period || "") + '</div></div>',
    inv.paidDate ? '<div class="date-cell"><div class="date-label">Paid On</div><div class="date-value" style="color:#22AA55">' + formatDate(inv.paidDate) + '</div></div>' : '',
    '</div>',
    '<div class="table-wrap"><table>',
    '<thead><tr><th>Description</th><th style="text-align:right">Amount</th></tr></thead>',
    '<tbody>' + itemRows + '</tbody>',
    '<tfoot><tr class="total-row"><td>Total Due</td><td>' + fmtC(inv.total) + '</td></tr></tfoot>',
    '</table></div>',
    inv.notes ? '<div class="notes"><strong>Note:</strong> ' + inv.notes + '</div>' : '',
    '<div class="payment">',
    '<div class="payment-title">Payment Instructions — e-Transfer</div>',
    '<div class="prow"><span class="pk">Send e-Transfer to:</span><span class="pv">' + MANAGER_EMAIL + '</span></div>',
    '<div class="prow"><span class="pk">Message / Reference:</span><span class="pv">' + (inv.number || "") + ' — ' + (inv.ownerName || "") + '</span></div>',
    '<div class="prow"><span class="pk">Amount Due:</span><span class="pv" style="font-weight:700;color:#E8622A">' + fmtC(inv.total) + '</span></div>',
    '<div class="prow"><span class="pk">Due By:</span><span class="pv">' + formatDate(inv.dueDate) + '</span></div>',
    '</div>',
    '<div class="footer">Generated by PropDesk · propdesk.ca · Property Management Software<br/>Thank you for your continued business.</div>',
    '</body></html>',
  ].join("\n");
};

// ── Sub-components ─────────────────────────────────────────────────────────
const EmptyState = ({ icon, title, subtitle }) => (
  <div style={{ padding: "48px 24px", textAlign: "center" }}>
    <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 13, color: "#9993A8" }}>{subtitle}</div>}
  </div>
);

const StatusChip = ({ status }) => {
  const map = { Draft: ["#ffffff10","#9993A8"], Sent: ["#2A7BE818","#2A7BE8"], Paid: ["#22AA5518","#22AA55"], Overdue: ["#FF2D2D18","#FF2D2D"] };
  const [bg, text] = map[status] || map.Draft;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: bg, color: text }}>{status}</span>;
};

// ── Main Component ─────────────────────────────────────────────────────────
const InvoicingTab = ({ properties = [], owners = [], invoices = [], onAddOwner, onAddInvoice, onUpdateInvoice }) => {
  const [subTab,        setSubTab]        = useState("invoices");
  const [showOwnerForm, setShowOwnerForm] = useState(false);
  const [showInvForm,   setShowInvForm]   = useState(false);
  const [expandedInv,   setExpandedInv]   = useState(null);

  const EMPTY_OWNER = { name: "", email: "", phone: "", propertyId: "", mgmtFee: "", notes: "" };
  const [ownerForm, setOwnerForm] = useState(EMPTY_OWNER);

  const EMPTY_INV = {
    ownerId: "", period: currentPeriod(), issueDate: today(), dueDate: addDays(14),
    items: [{ description: "Monthly Management Fee", amount: "" }],
    notes: "",
  };
  const [invForm, setInvForm] = useState(EMPTY_INV);

  // Computed
  const totalCollected   = invoices.filter((i) => i.status === "Paid").reduce((s, i) => s + i.total, 0);
  const totalOutstanding = invoices.filter((i) => ["Sent","Overdue"].includes(i.status)).reduce((s, i) => s + i.total, 0);
  const overdueCount     = invoices.filter((i) => i.status === "Overdue").length;
  const invTotal         = invForm.items.reduce((s, item) => s + (parseFloat(item.amount) || 0), 0);

  // Owner handlers
  const handleAddOwner = async () => {
    if (!ownerForm.name) return;
    const ownerData = {
      ...ownerForm,
      propertyId: parseInt(ownerForm.propertyId) || null,
      mgmtFee: parseFloat(ownerForm.mgmtFee) || 0,
    };
    if (onAddOwner) await onAddOwner(ownerData);
    setOwnerForm(EMPTY_OWNER);
    setShowOwnerForm(false);
  };

  // Line item handlers
  const addItem    = () => setInvForm((f) => ({ ...f, items: [...f.items, { description: "", amount: "" }] }));
  const removeItem = (i) => setInvForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));
  const updateItem = (i, field, val) => setInvForm((f) => ({ ...f, items: f.items.map((item, j) => j === i ? { ...item, [field]: val } : item) }));

  // Invoice handlers
  const handleCreateInvoice = async (status = "Draft") => {
    const owner = owners.find((o) => String(o.id) === String(invForm.ownerId));
    if (!owner) return;
    const property = properties.find((p) => p.id === owner.propertyId);
    const newInv = {
      ownerId:      owner.id,
      number:       nextInvoiceNum(invoices),
      period:       invForm.period,
      issueDate:    invForm.issueDate,
      dueDate:      invForm.dueDate,
      items:        invForm.items,
      total:        invTotal,
      status,
      notes:        invForm.notes || "",
      stripePaymentLink: invForm.stripePaymentLink || "",
      // display fields (not saved to DB but used in UI)
      ownerName:    owner.name,
      ownerEmail:   owner.email,
      property:     property?.address || property?.name || "—",
    };
    if (onAddInvoice) await onAddInvoice(newInv);
    setInvForm(EMPTY_INV);
    setShowInvForm(false);
    setSubTab("invoices");
  };

  const handleMarkPaid = async (invId, method) => {
    const updates = { status: "Paid", paidDate: today(), paymentMethod: method };
    if (onUpdateInvoice) await onUpdateInvoice(invId, updates);
  };

  const handleMarkOverdue = async (invId) => {
    if (onUpdateInvoice) await onUpdateInvoice(invId, { status: "Overdue" });
  };

  const handleDownload = (inv) => {
    const blob = new Blob([generateInvoiceHtml(inv)], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `${inv.number}_${(inv.ownerName || "invoice").replace(/\s+/g, "_")}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };



  const tabs = [{ id: "invoices", label: "Invoices" }, { id: "owners", label: "Property Owners" }];

  return (
    <div>
      <div style={S.pageTitle}>Invoicing</div>
      <div style={S.pageSub}>Monthly invoices to property owners — payment via e-Transfer</div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 14, marginBottom: 28 }}>
        {[
          { label: "Total Collected",  value: fmtC(totalCollected),   color: "#22AA55" },
          { label: "Outstanding",      value: fmtC(totalOutstanding), color: "#E8622A" },
          { label: "Overdue",          value: overdueCount,           color: "#FF2D2D" },
          { label: "Total Invoices",   value: invoices.length,        color: "#2A7BE8" },
        ].map((st) => (
          <div key={st.label} style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: st.color }}>{st.value}</div>
            <div style={{ fontSize: 11, color: "#9993A8", fontWeight: 600, marginTop: 4 }}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs + action */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div style={{ ...S.tabRow, maxWidth: 320, marginBottom: 0 }}>
          {tabs.map(({ id, label }) => (
            <button key={id} style={{ ...S.tabBtn, ...(subTab === id ? S.tabBtnActive : {}) }} onClick={() => setSubTab(id)}>{label}</button>
          ))}
        </div>
        {subTab === "invoices" && (
          <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 16px", opacity: owners.length ? 1 : 0.5 }} onClick={() => owners.length && setShowInvForm(true)}>
            + New Invoice
          </button>
        )}
        {subTab === "owners" && (
          <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 16px" }} onClick={() => setShowOwnerForm(true)}>
            + Add Owner
          </button>
        )}
      </div>

      {/* ── INVOICES TAB ── */}
      {subTab === "invoices" && (
        <div>
          {showInvForm && (
            <div style={{ background: "#141418", border: "1px solid #E8622A44", borderRadius: 14, padding: "24px", marginBottom: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 20 }}>New Invoice</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
                <div style={S.field}>
                  <label style={S.label}>Bill To *</label>
                  <select style={S.select} value={invForm.ownerId} onChange={(e) => {
                    const owner = owners.find((o) => String(o.id) === e.target.value);
                    setInvForm((f) => ({ ...f, ownerId: e.target.value, items: owner?.mgmtFee ? [{ description: "Monthly Management Fee", amount: owner.mgmtFee }] : f.items }));
                  }}>
                    <option value="">-- Select owner --</option>
                    {owners.map((o) => {
                      const prop = properties.find((p) => p.id === o.propertyId);
                      return <option key={o.id} value={o.id}>{o.name} — {prop?.address || "No property"}</option>;
                    })}
                  </select>
                </div>
                <div style={S.field}>
                  <label style={S.label}>Service Period</label>
                  <input style={S.input} value={invForm.period} onChange={(e) => setInvForm((f) => ({ ...f, period: e.target.value }))} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Due Date</label>
                  <input style={S.input} type="date" value={invForm.dueDate} onChange={(e) => setInvForm((f) => ({ ...f, dueDate: e.target.value }))} />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ ...S.label, marginBottom: 10 }}>Line Items</label>
                {invForm.items.map((item, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 140px 36px", gap: 8, marginBottom: 8 }}>
                    <input style={S.input} placeholder="Description" value={item.description} onChange={(e) => updateItem(i, "description", e.target.value)} />
                    <input style={{ ...S.input, textAlign: "right" }} type="number" placeholder="Amount" value={item.amount} onChange={(e) => updateItem(i, "amount", e.target.value)} />
                    <button style={{ background: "#FF2D2D18", border: "none", borderRadius: 8, color: "#FF2D2D", cursor: "pointer", fontSize: 18 }} onClick={() => removeItem(i)}>x</button>
                  </div>
                ))}
                <button style={{ ...S.btnSecondary, fontSize: 12, padding: "6px 14px", marginTop: 4 }} onClick={addItem}>+ Add Line Item</button>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                <div style={{ background: "#0F0F14", borderRadius: 10, padding: "12px 20px", textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#9993A8", marginBottom: 2 }}>TOTAL DUE</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "#E8622A" }}>{fmtC(invTotal)}</div>
                </div>
              </div>

              <div style={{ ...S.field, marginBottom: 16 }}>
                <label style={S.label}>Notes (optional)</label>
                <textarea style={{ ...S.textarea, minHeight: 60 }} placeholder="Additional notes..." value={invForm.notes} onChange={(e) => setInvForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>

              <div style={{ background: "#E8622A0D", border: "1px solid #E8622A30", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#E8622A", marginBottom: 3 }}>e-Transfer Instructions</div>
                <div style={{ fontSize: 12, color: "#9993A8" }}>Invoice will instruct owner to send e-Transfer to <strong style={{ color: "#F0EDE8" }}>{MANAGER_EMAIL}</strong> with the invoice number as reference.</div>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button style={{ ...S.btnPrimary, padding: "10px 20px", fontSize: 13 }} onClick={() => handleCreateInvoice("Draft")}>Save as Draft</button>
                <button style={{ ...S.btnPrimary, background: "#2A7BE8", padding: "10px 20px", fontSize: 13 }} onClick={() => handleCreateInvoice("Sent")}>Save & Mark Sent</button>
                <button style={{ ...S.btnSecondary, padding: "10px 20px", fontSize: 13 }} onClick={() => setShowInvForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={S.tableWrap}>
            <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 130px 100px 90px 200px", gap: 12, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
              {["Number", "Owner / Property", "Period", "Total", "Status", "Actions"].map((h) => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
              ))}
            </div>

            {invoices.map((inv) => (
              <div key={inv.id}>
                <div
                  style={{ display: "grid", gridTemplateColumns: "100px 1fr 130px 100px 90px 200px", gap: 12, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center", cursor: "pointer" }}
                  onClick={() => setExpandedInv(expandedInv === inv.id ? null : inv.id)}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#E8622A" }}>{inv.number}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{inv.ownerName}</div>
                    <div style={{ fontSize: 11, color: "#9993A8" }}>{inv.property}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>{inv.period}</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{fmtC(inv.total)}</div>
                  <StatusChip status={inv.status} />
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#9993A8", fontSize: 11, padding: "5px 9px" }} onClick={(e) => { e.stopPropagation(); handleDownload(inv); }}>Download</button>

                    {inv.status !== "Paid" && (
                      <button style={{ ...S.btnSmall, background: "#22AA5518", color: "#22AA55", fontSize: 11, padding: "5px 9px" }} onClick={(e) => { e.stopPropagation(); handleMarkPaid(inv.id, "e-Transfer"); }}>Paid</button>
                    )}
                  </div>
                </div>

                {expandedInv === inv.id && (
                  <div style={{ padding: "20px", background: "#0F0F14", borderBottom: "1px solid #ffffff06" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Line Items</div>
                        {(inv.items || []).map((item, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #ffffff06", fontSize: 13 }}>
                            <span style={{ color: "#9993A8" }}>{item.description}</span>
                            <span style={{ fontWeight: 600 }}>{fmtC(item.amount)}</span>
                          </div>
                        ))}
                        <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontSize: 14, fontWeight: 800, color: "#E8622A" }}>
                          <span>Total</span><span>{fmtC(inv.total)}</span>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>Details</div>
                        {[
                          ["Issue Date",     formatDate(inv.issueDate)],
                          ["Due Date",       formatDate(inv.dueDate)],
                          ["Paid On",        inv.paidDate ? formatDate(inv.paidDate) : "—"],
                          ["Payment Method", inv.paymentMethod || "—"],
                        ].map(([label, value]) => (
                          <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 12 }}>
                            <span style={{ color: "#9993A8" }}>{label}</span>
                            <span style={{ color: "#F0EDE8" }}>{value}</span>
                          </div>
                        ))}
                        {inv.notes && <div style={{ marginTop: 8, fontSize: 12, color: "#9993A8", fontStyle: "italic" }}>{inv.notes}</div>}
                        {inv.status !== "Paid" && (
                          <div style={{ marginTop: 14 }}>
                            <div style={{ fontSize: 11, color: "#9993A8", marginBottom: 8 }}>Mark as paid via:</div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                              {["e-Transfer", "Cash", "Cheque", "Other"].map((method) => (
                                <button key={method} style={{ ...S.btnSmall, background: "#22AA5518", color: "#22AA55", fontSize: 11, padding: "5px 10px" }} onClick={() => handleMarkPaid(inv.id, method)}>{method}</button>
                              ))}
                            </div>
                          </div>
                        )}
                        {inv.status === "Sent" && (
                          <button style={{ ...S.btnSmall, background: "#FF2D2D18", color: "#FF2D2D", fontSize: 11, padding: "5px 10px", marginTop: 10 }} onClick={() => handleMarkOverdue(inv.id)}>Mark Overdue</button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {invoices.length === 0 && (
              <EmptyState icon="🧾" title="No invoices yet" subtitle={owners.length ? "Click + New Invoice above" : "Add a property owner first"} />
            )}
          </div>
        </div>
      )}

      {/* ── OWNERS TAB ── */}
      {subTab === "owners" && (
        <div>
          {showOwnerForm && (
            <div style={{ background: "#141418", border: "1px solid #E8622A44", borderRadius: 14, padding: "24px", marginBottom: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>New Property Owner</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div><label style={S.label}>Full Name *</label><input style={S.input} placeholder="Owner name" value={ownerForm.name} onChange={(e) => setOwnerForm({ ...ownerForm, name: e.target.value })} /></div>
                <div><label style={S.label}>Email *</label><input style={S.input} placeholder="owner@email.com" value={ownerForm.email} onChange={(e) => setOwnerForm({ ...ownerForm, email: e.target.value })} /></div>
                <div><label style={S.label}>Phone</label><input style={S.input} placeholder="604-555-0000" value={ownerForm.phone} onChange={(e) => setOwnerForm({ ...ownerForm, phone: e.target.value })} /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={S.label}>Property</label>
                  <select style={S.select} value={ownerForm.propertyId} onChange={(e) => setOwnerForm({ ...ownerForm, propertyId: e.target.value })}>
                    <option value="">-- Select --</option>
                    {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
                  </select>
                </div>
                <div><label style={S.label}>Monthly Mgmt Fee ($)</label><input style={S.input} type="number" placeholder="e.g. 349" value={ownerForm.mgmtFee} onChange={(e) => setOwnerForm({ ...ownerForm, mgmtFee: e.target.value })} /></div>
                <div><label style={S.label}>Notes</label><input style={S.input} placeholder="Optional" value={ownerForm.notes} onChange={(e) => setOwnerForm({ ...ownerForm, notes: e.target.value })} /></div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ ...S.btnPrimary, padding: "9px 20px", fontSize: 13 }} onClick={handleAddOwner}>Save Owner</button>
                <button style={{ ...S.btnSecondary, padding: "9px 20px", fontSize: 13 }} onClick={() => setShowOwnerForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={S.tableWrap}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 120px 110px", gap: 12, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
              {["Owner", "Contact", "Property", "Fee / Month", ""].map((h) => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
              ))}
            </div>
            {owners.map((o) => {
              const prop = properties.find((p) => p.id === o.propertyId);
              return (
                <div key={o.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 120px 110px", gap: 12, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{o.name}</div>
                    {o.notes && <div style={{ fontSize: 11, color: "#5A5570" }}>{o.notes}</div>}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#9993A8" }}>{o.email || "No email"}</div>
                    <div style={{ fontSize: 12, color: "#9993A8" }}>{o.phone}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>{prop?.address || prop?.name || "—"}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#22AA55" }}>{o.mgmtFee ? fmtC(o.mgmtFee) : "—"}</div>
                  <button
                    style={{ ...S.btnSmall, background: "#E8622A18", color: "#E8622A", fontSize: 11, padding: "6px 12px" }}
                    onClick={() => { setInvForm((f) => ({ ...f, ownerId: String(o.id), items: o.mgmtFee ? [{ description: "Monthly Management Fee", amount: o.mgmtFee }] : f.items })); setShowInvForm(true); setSubTab("invoices"); }}
                  >
                    + Invoice
                  </button>
                </div>
              );
            })}
            {owners.length === 0 && <EmptyState icon="🏠" title="No owners yet" subtitle="Add property owners to start invoicing" />}
          </div>

          {owners.length > 0 && (
            <div style={{ marginTop: 20, background: "#141418", border: "1px solid #ffffff08", borderRadius: 14, padding: "20px 24px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Management Fee Summary</div>
              <div style={{ display: "flex", gap: 32 }}>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#22AA55" }}>{fmtC(owners.reduce((s, o) => s + (parseFloat(o.mgmtFee) || 0), 0))}</div>
                  <div style={{ fontSize: 12, color: "#9993A8", marginTop: 2 }}>per month</div>
                </div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#E8622A" }}>{fmtC(owners.reduce((s, o) => s + (parseFloat(o.mgmtFee) || 0), 0) * 12)}</div>
                  <div style={{ fontSize: 12, color: "#9993A8", marginTop: 2 }}>annualized</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoicingTab;
